/**
 * The assistant's client (agent-1-web W1): four REST calls and the message
 * stream, typed from the backend's contract (`ChatStreamEvent` and its six
 * payloads in `contract/openapi.json`).
 *
 * The reply is never streamed token by token. A turn is one POST answered by a
 * `text/event-stream` body: `session`, any number of `tool_step`s, then
 * `reply` or `fallback`, then `done` -- or, in place of the terminal pair, an
 * `error` (`internal.error`, never retryable: reload the conversation). That
 * order is the backend's `sse.check_sequence`; `SequenceGuard` below holds the
 * stream to the same rule, so a stream that breaks it is a protocol error the
 * UI can show, never a half-rendered turn.
 *
 * `EventSource` cannot POST, so this is `fetch` and a `ReadableStream` reader.
 * Everything before the first byte of the stream is a Problem with a status
 * (agent-1 C46): 400, 404, 409 `chat.turn_in_progress`, 429 `chat.rate_limited`
 * and 503 `chat.busy`, the last two with `Retry-After` (exposed through CORS).
 */
import type { components } from './api.types'
import { API_BASE, getHeaders, query, request, throwApiError } from './api'
import { ProblemError, type Problem } from './problem'

type Schemas = components['schemas']

export type ChatContext = Schemas['Context']
export type ChatRoute = ChatContext['route']
export type ChatSession = Schemas['ChatSession']
export type ChatSessionPage = Schemas['ChatSessionPage']
export type ChatSessionDetail = Schemas['ChatSessionDetail']
export type ChatMessage = Schemas['ChatMessage']
export type ChatCard = Schemas['ChatCard']
export type ChatClaim = Schemas['ChatClaim']
export type ChatToolStep = Schemas['ChatToolStep']
export type FallbackEvent = Schemas['FallbackEvent']
export type ChatStreamEvent = Schemas['ChatStreamEvent']
export type FallbackReason = FallbackEvent['reason']

/**
 * The client's ceiling on one turn (agent-1 C72, WC5). It must exceed the
 * server's `chat_timeout_s` -- 8 s today, allowed up to 30 s -- so the server
 * always speaks first: its own timeout becomes the `timeout` wording, a stored
 * turn, rather than a stream the client walked away from.
 */
export const TURN_CEILING_MS = 35_000

const SESSIONS = '/api/v1/chat/sessions'
const session = (id: string) => `${SESSIONS}/${encodeURIComponent(id)}`

export const createSession = (context: ChatContext, signal?: AbortSignal) =>
  request<ChatSession>(SESSIONS, { method: 'POST', body: { context }, signal })

export const listSessions = (p: { cursor?: string | null; limit?: number } = {}, signal?: AbortSignal) =>
  request<ChatSessionPage>(`${SESSIONS}${query({ ...p })}`, { signal })

export const getSession = (
  id: string,
  p: { before?: string | null; limit?: number } = {},
  signal?: AbortSignal,
) => request<ChatSessionDetail>(`${session(id)}${query({ ...p })}`, { signal })

export const deleteSession = (id: string) => request<void>(session(id), { method: 'DELETE' })

/** Replace this person's usefulness judgement of one completed explanation (EXP-011). */
export const setMessageFeedback = (sessionId: string, messageId: string, useful: boolean) =>
  request<void>(`${session(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`, {
    method: 'POST',
    body: { useful } satisfies Schemas['ChatMessageFeedbackCreate'],
  })

// ── the stream ────────────────────────────────────────────────────────────

/** A stream that broke the contract: an unknown event, bad JSON, or the wrong order. */
export class StreamProtocolError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'StreamProtocolError'
  }
}

/**
 * The stream ended before its terminal event: the connection dropped, or the
 * client's ceiling fired. The turn may well be stored -- the remedy is to send
 * the SAME `client_message_id` again, which replays a finished turn or answers
 * 409 while it is still being written (C48, C69).
 */
export class StreamDroppedError extends Error {
  readonly why: 'ceiling' | 'ended' | 'network'

  constructor(why: 'ceiling' | 'ended' | 'network') {
    super(`the reply stream stopped before it finished (${why})`)
    this.name = 'StreamDroppedError'
    this.why = why
  }
}

/**
 * A Problem answered before the stream, with `Retry-After` when the server
 * sent one. `ProblemError` alone would lose the header.
 */
export class ChatRefusedError extends ProblemError {
  /** Whole seconds from `Retry-After`, or null when the server sent none. */
  readonly retryAfterS: number | null

  constructor(problem: Problem, retryAfterS: number | null) {
    super(problem)
    this.name = 'ChatRefusedError'
    this.retryAfterS = retryAfterS
  }
}

const EVENT_NAMES = new Set(['session', 'tool_step', 'reply', 'fallback', 'error', 'done'])

/**
 * An incremental `text/event-stream` parser over already-decoded text.
 *
 * The server writes one `event:` line, one `data:` line of compact JSON and a
 * blank line per frame (`brainspot/api/sse.py`), but a chunk boundary can fall
 * anywhere -- inside a line, between the two lines, inside a multi-byte
 * character (the decoder in `sendMessage` runs with `stream: true` for that).
 * So text is buffered until a blank line closes a frame. CRLF is accepted
 * because the SSE grammar allows it; a comment line (`:`) is ignored.
 */
export class SseParser {
  private buffer = ''

  /** Every complete frame in what has arrived so far. */
  push(text: string): ChatStreamEvent[] {
    return [...this.feed(text)]
  }

  /**
   * The same, one frame at a time, so a reader can deliver each frame before
   * the next is parsed: a malformed frame then stops the stream AFTER the good
   * frames ahead of it, never before them (W1 review).
   */
  *feed(text: string): Generator<ChatStreamEvent> {
    // A CR at the very end may be half of a CRLF split across chunks: it waits
    // for the next chunk rather than becoming a line break of its own (W1 review).
    let incoming = `${this.buffer}${text}`
    let held = ''
    if (incoming.endsWith('\r')) {
      held = '\r'
      incoming = incoming.slice(0, -1)
    }
    // Every complete frame ends in a blank line; what follows the last one is
    // the start of a frame still arriving, and waits for the next chunk.
    const parts = incoming.replace(/\r\n?/g, '\n').split('\n\n')
    this.buffer = `${parts.pop() ?? ''}${held}`
    for (const raw of parts) {
      const frame = parseFrame(raw)
      if (frame) yield frame
    }
  }
}

function parseFrame(raw: string): ChatStreamEvent | null {
  let event: string | null = null
  const data: string[] = []
  for (const line of raw.split('\n')) {
    if (line === '' || line.startsWith(':')) continue
    // `field: value`, the one optional space after the colon not part of the value.
    const [, field, value = ''] = /^([^:]*)(?::(?: ?)(.*))?$/s.exec(line) ?? []
    if (field === 'event') event = value
    else if (field === 'data') data.push(value)
  }
  if (event === null && data.length === 0) return null
  if (event === null || !EVENT_NAMES.has(event)) {
    throw new StreamProtocolError(`unknown event ${JSON.stringify(event)}`)
  }
  let payload: unknown
  try {
    payload = JSON.parse(data.join('\n'))
  } catch {
    throw new StreamProtocolError(`the ${event} event's data is not JSON`)
  }
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    throw new StreamProtocolError(`the ${event} event's data is not an object`)
  }
  return { event, data: payload } as ChatStreamEvent
}

/**
 * The backend's `check_sequence`, client side:
 * `session → tool_step* → (reply | fallback) → done`, or `session → tool_step* → error`.
 */
export class SequenceGuard {
  private phase: 'start' | 'open' | 'answered' | 'closed' = 'start'
  private assistantMessageId: string | null = null

  accept(frame: ChatStreamEvent): void {
    const { event } = frame
    // The answer must be the message `session` announced (`check_sequence`
    // refuses any other): a reply under another id would leave the announced
    // one "thinking" forever, with nothing on screen to say so.
    if ((frame.event === 'reply' || frame.event === 'fallback') && frame.data.message_id !== this.assistantMessageId) {
      throw new StreamProtocolError(`${event} answers ${frame.data.message_id}, not the announced message`)
    }
    if (frame.event === 'session') this.assistantMessageId = frame.data.assistant_message_id
    const ok =
      (this.phase === 'start' && event === 'session') ||
      (this.phase === 'open' && (event === 'tool_step' || event === 'reply' || event === 'fallback' || event === 'error')) ||
      (this.phase === 'answered' && event === 'done')
    if (!ok) throw new StreamProtocolError(`${event} cannot follow ${this.phase === 'start' ? 'nothing' : this.phase}`)
    if (event === 'session') this.phase = 'open'
    else if (event === 'reply' || event === 'fallback') this.phase = 'answered'
    else if (event === 'done' || event === 'error') this.phase = 'closed'
  }

  get closed(): boolean {
    return this.phase === 'closed'
  }
}

function retryAfter(res: Response): number | null {
  const value = res.headers.get('Retry-After')
  if (value === null || !/^\d+$/.test(value.trim())) return null
  return Number(value.trim())
}

export interface SendMessageArgs {
  sessionId: string
  text: string
  context: ChatContext
  /** A UUID per message; the same one again replays a finished turn (C48). */
  clientMessageId: string
  onEvent: (frame: ChatStreamEvent) => void
  signal?: AbortSignal
  ceilingMs?: number
}

/**
 * Send one message and deliver its events in order. Resolves when the stream
 * closes on `done` or `error`. Rejects with `ChatRefusedError` for a Problem
 * before the stream, `StreamProtocolError` for a stream that broke the
 * contract, `StreamDroppedError` when it stopped early or the ceiling fired,
 * and the caller's own `AbortError` when the caller aborted.
 */
export async function sendMessage({
  sessionId,
  text,
  context,
  clientMessageId,
  onEvent,
  signal,
  ceilingMs = TURN_CEILING_MS,
}: SendMessageArgs): Promise<void> {
  const controller = new AbortController()
  let ceilingHit = false
  const timer = setTimeout(() => {
    ceilingHit = true
    controller.abort()
  }, ceilingMs)
  const onAbort = () => controller.abort()
  if (signal?.aborted) controller.abort()
  else signal?.addEventListener('abort', onAbort, { once: true })

  const dropped = (why: StreamDroppedError['why']) => {
    if (signal?.aborted) return new DOMException('The operation was aborted.', 'AbortError')
    return new StreamDroppedError(ceilingHit ? 'ceiling' : why)
  }

  try {
    let res: Response
    try {
      const headers = await getHeaders()
      headers.Accept = 'text/event-stream'
      res = await fetch(`${API_BASE}${session(sessionId)}/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text, context, client_message_id: clientMessageId }),
        signal: controller.signal,
      })
    } catch {
      throw dropped('network')
    }

    if (!res.ok) {
      const seconds = retryAfter(res)
      try {
        await throwApiError(res)
      } catch (err) {
        if (err instanceof ProblemError && err.problem) throw new ChatRefusedError(err.problem, seconds)
        throw err
      }
    }
    if (!res.body) throw new StreamDroppedError('ended')
    // A 200 that is not a stream -- a proxy's page, an SPA fallback behind a
    // wrong API origin -- is a broken contract, not a drop to resend (final review).
    if (!(res.headers.get('Content-Type') ?? '').startsWith('text/event-stream')) {
      throw new StreamProtocolError('the reply is not an event stream')
    }

    const reader = res.body.getReader()
    // `fetch` ties its signal to the body, but not every transport does (a
    // service worker's stream, a test double). Cancelling the reader on abort
    // makes the ceiling and the caller's abort work whatever is underneath:
    // the pending read resolves `done`, and `dropped` names why.
    const stop = () => {
      reader.cancel().catch(() => {})
    }
    controller.signal.addEventListener('abort', stop, { once: true })
    // Aborted before the body was reached (an already-aborted signal, a ceiling
    // that fired during the request): no listener will ever run, so stop now.
    if (controller.signal.aborted) stop()
    const decoder = new TextDecoder('utf-8')
    const parser = new SseParser()
    const guard = new SequenceGuard()
    try {
      for (;;) {
        let chunk: ReadableStreamReadResult<Uint8Array>
        try {
          chunk = await reader.read()
        } catch {
          throw dropped('network')
        }
        const textPart = chunk.done ? decoder.decode() : decoder.decode(chunk.value, { stream: true })
        for (const frame of parser.feed(textPart)) {
          guard.accept(frame)
          onEvent(frame)
          if (guard.closed) return
        }
        if (chunk.done) throw dropped('ended')
      }
    } finally {
      controller.signal.removeEventListener('abort', stop)
      reader.cancel().catch(() => {})
    }
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  }
}
